import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiBriefcase, FiCalendar, FiMapPin, FiAward, FiArrowRight } from 'react-icons/fi';
import ProfileTopSection from '../components/ProfileTopSection';
import About from '../components/About';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0 },
};

// Preview: latest 2 experiences
const experiencesPreview = [
  {
    title: 'Sr. SQA Engineer',
    company: 'Exact Solution Electronics',
    location: 'Warsaw, Mazowieckie, Poland · Remote',
    period: 'Mar 2026 - Present',
    type: 'Full-time',
    achievements: [
      'Performing end-to-end manual testing of ExactFlow, a complex multi-marketplace e-commerce platform',
      'Conducting functional, integration, regression, and edge-case testing for WMS workflows including picking, packing, stocktake, and inventory reconciliation',
    ],
    technologies: ['Manual Testing', 'Functional Testing', 'Regression Testing', 'WMS', 'E-commerce'],
  },
  {
    title: 'SQA Engineer',
    company: 'BugVerse',
    location: 'Lahore, Pakistan',
    period: 'Oct 2025 - Mar 2026',
    type: 'Full-time',
    achievements: [
      'Owned QA execution for healthcare and AI-based web applications; validated features against requirements',
      'Designed and maintained Cypress automated tests using standard automation design patterns',
    ],
    technologies: ['Cypress', 'JavaScript', 'Postman', 'REST APIs', 'JIRA'],
  },
];

// Preview: top 3 skill categories
const skillsPreview = [
  {
    category: 'Testing Methodologies',
    skills: ['Manual Testing', 'Automated Testing', 'Functional Testing', 'Regression Testing'],
  },
  {
    category: 'Test Automation',
    skills: ['Cypress', 'JavaScript'],
  },
  {
    category: 'DevOps & CI/CD',
    skills: ['DevOps', 'Continuous Integration', 'CI/CD Pipelines', 'Automation'],
  },
];

// Preview: 3 featured projects
const projectsPreview = [
  {
    title: 'ExactFlow',
    description: 'Multi-marketplace e-commerce platform handling product listings, stock synchronization, order processing, invoicing, and WMS workflows at Exact Solution Electronics.',
    category: 'E-commerce',
    technologies: ['Manual Testing', 'Regression Testing', 'Integration Testing', 'WMS'],
  },
  {
    title: 'RIS / LAB',
    description: 'Radiology Information System — Healthcare application for patient registration, role-based access control, PACS workflows.',
    category: 'Healthcare',
    technologies: ['Cypress', 'SQL', 'RBAC'],
  },
  {
    title: 'Creative AI Agents Marketplace',
    description: 'AI-agent marketplace platform for text, audio, and image generation pipelines with role-based access.',
    category: 'AI/Automation',
    technologies: ['Cypress', 'JavaScript', 'AI Agents'],
  },
];

const ExperiencePreview = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section className="section-padding bg-gradient-to-br from-secondary-50 to-primary-50 dark:from-dark-50 dark:to-dark-100 transition-colors duration-300">
      <div className="container-custom">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-secondary-800 dark:text-white mb-4">
            Work <span className="gradient-text">Experience</span>
          </h2>
          <p className="text-lg text-secondary-600 dark:text-gray-100 max-w-2xl mx-auto">
            My recent professional journey in SQA and test automation.
          </p>
        </motion.div>

        <div className="space-y-8">
          {experiencesPreview.map((exp, index) => (
            <motion.div
              key={`${exp.company}-${exp.title}`}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: index * 0.2 }}
              className="relative"
            >
              {index < experiencesPreview.length - 1 && (
                <div className="absolute top-6 left-[24px] w-0.5 h-full bg-gradient-to-b from-primary-500 to-transparent z-0" />
              )}
              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-shrink-0 relative z-10" style={{ width: '48px' }}>
                  <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-accent-600 rounded-full flex items-center justify-center shadow-lg">
                    <FiBriefcase className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="flex-1 bg-white/80 dark:bg-dark-200/80 rounded-xl p-6 shadow-lg z-10">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-secondary-800 dark:text-white mb-1">{exp.title}</h3>
                      <p className="text-lg font-semibold text-primary-600 dark:text-primary-400">{exp.company}</p>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-secondary-600 dark:text-gray-100 mt-2 md:mt-0">
                      <div className="flex items-center gap-1"><FiCalendar className="w-4 h-4" /><span>{exp.period}</span></div>
                      <div className="flex items-center gap-1"><FiMapPin className="w-4 h-4" /><span>{exp.location}</span></div>
                      <div className="flex items-center gap-1"><FiAward className="w-4 h-4" /><span>{exp.type}</span></div>
                    </div>
                  </div>
                  <ul className="space-y-2 mb-4">
                    {exp.achievements.map((a, i) => (
                      <li key={i} className="flex items-start space-x-2 text-secondary-600 dark:text-gray-100">
                        <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm">{a}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-wrap gap-2">
                    {exp.technologies.map((tech) => (
                      <span key={tech} className="px-3 py-1 bg-primary-100 dark:bg-dark-300 text-primary-600 dark:text-primary-400 text-xs font-medium rounded-full border border-primary-200 dark:border-dark-300">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/experience"
            className="inline-flex items-center gap-2 px-8 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors duration-200"
          >
            View Full Experience <FiArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

const SkillsPreview = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section className="section-padding bg-white dark:bg-dark-100 transition-colors duration-300">
      <div className="container-custom">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-secondary-800 dark:text-white mb-4">
            Technical <span className="gradient-text">Skills</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {skillsPreview.map((section, idx) => (
            <motion.div
              key={section.category}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: idx * 0.15 }}
              className="bg-white/80 dark:bg-dark-200/80 border border-primary-200 dark:border-primary-600 rounded-2xl shadow-lg p-6 flex flex-col items-center text-center backdrop-blur-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h3 className="text-lg font-semibold mb-4 text-primary-700 dark:text-white">{section.category}</h3>
              <div className="flex flex-wrap gap-2 justify-center">
                {section.skills.map((skill) => (
                  <span key={skill} className="px-3 py-1 rounded-full bg-primary-100 dark:bg-primary-700 text-primary-700 dark:text-primary-200 text-sm font-medium shadow-sm border border-primary-200 dark:border-primary-400">
                    {skill}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/skills"
            className="inline-flex items-center gap-2 px-8 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors duration-200"
          >
            View All Skills <FiArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

const ProjectsPreview = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section className="section-padding bg-gradient-to-br from-secondary-50 to-primary-50 dark:from-dark-50 dark:to-dark-100 transition-colors duration-300">
      <div className="container-custom">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-secondary-800 dark:text-white mb-4">
            Featured <span className="gradient-text">Projects</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {projectsPreview.map((project, index) => (
            <motion.div
              key={project.title}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.15 }}
              className="bg-white/80 dark:bg-dark-200/80 border border-primary-100 dark:border-dark-300 rounded-2xl shadow-lg p-6 flex flex-col"
            >
              <span className="text-xs font-semibold px-3 py-1 rounded-full bg-primary-100 dark:bg-primary-700 text-primary-700 dark:text-primary-200 self-start mb-3">
                {project.category}
              </span>
              <h3 className="text-xl font-bold mb-2 text-secondary-800 dark:text-white">{project.title}</h3>
              <p className="text-base text-secondary-600 dark:text-gray-100 mb-4 flex-1">{project.description}</p>
              <div className="flex flex-wrap gap-2">
                {project.technologies.map((tech, i) => (
                  <span key={i} className="px-3 py-1 bg-primary-100 dark:bg-primary-700 text-primary-700 dark:text-primary-200 text-xs font-medium rounded-full border border-primary-200 dark:border-primary-400">
                    {tech}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/projects"
            className="inline-flex items-center gap-2 px-8 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors duration-200"
          >
            View All Projects <FiArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

const CTABanner = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section className="section-padding bg-white dark:bg-dark-100 transition-colors duration-300">
      <div className="container-custom">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-r from-primary-600 to-accent-600 rounded-2xl p-8 md:p-12 text-center shadow-xl"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Interested in working together?
          </h2>
          <p className="text-primary-100 text-lg mb-8 max-w-2xl mx-auto">
            I'm always open to new opportunities and collaborations. Let's discuss how I can help ensure the quality of your next project.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-3 bg-white text-primary-600 rounded-lg font-semibold hover:bg-primary-50 transition-colors duration-200 shadow-lg"
          >
            Get In Touch <FiArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

const HomePage = () => {
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.4 }}
    >
      <ProfileTopSection />
      <About />
      <ExperiencePreview />
      <SkillsPreview />
      <ProjectsPreview />
      <CTABanner />
    </motion.div>
  );
};

export default HomePage;
